<template>
  <div class="barcode-scanner">
    <label for="barcode-input" class="barcode-label">
      <i class="pi pi-barcode"></i>
      <span>Scan RapID Tag or RFID Tag</span>
    </label>
    <div class="input-wrapper">
      <input
        id="barcode-input"
        ref="barcodeInput"
        v-model="barcodeValue"
        type="text"
        class="barcode-input"
        placeholder="Scan RapID or RFID"
        :maxlength="SCAN_TOKEN_MAX_LENGTH"
        autocomplete="off"
        inputmode="none"
        @keyup.enter="handleScan"
        @input="handleInput"
        @touchstart.prevent="handleTouch"
        @touchend.prevent="handleTouch"
      />
      <div v-if="normalizedBarcode" class="input-indicator valid">
        <i class="pi pi-check"></i>
      </div>
      <div v-else-if="error" class="input-indicator error">
        <i class="pi pi-times"></i>
      </div>
    </div>
    <div class="error-container">
      <div v-if="error" class="error-message">
        <i class="pi pi-exclamation-circle"></i>
        <span>{{ error }}</span>
      </div>
    </div>
    <div class="button-group">
      <button type="button" class="btn-clear" :disabled="!barcodeValue" @click="handleClear">
        <i class="pi pi-times"></i>
        <span>Clear</span>
      </button>
      <button
        type="button"
        class="btn-confirm"
        :disabled="!normalizedBarcode || !!error"
        @click="handleScan"
      >
        <i class="pi pi-check"></i>
        <span>Confirm</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { normalizeBarcode } from '../../services/audit.service';
import {
  SCAN_TOKEN_MAX_LENGTH,
  SCAN_TOKEN_ERROR_MESSAGE,
  shouldShowScanTokenError,
} from '../../constants/scan-token.constants';

const emit = defineEmits<{
  scan: [barcode: string];
  error: [message: string];
}>();

const barcodeValue = ref('');
const normalizedBarcode = ref<string | null>(null);
const error = ref<string | null>(null);
const barcodeInput = ref<HTMLInputElement | null>(null);
const shouldShowInputError = computed(() => shouldShowScanTokenError(barcodeValue.value));

/**
 * Handle clear button click
 */
function handleClear() {
  reset();
}

/**
 * Handle input changes - normalize and validate in real-time
 */
function handleInput() {
  error.value = null;
  normalizedBarcode.value = null;

  if (!barcodeValue.value.trim()) {
    return;
  }

  const normalized = normalizeBarcode(barcodeValue.value);
  if (normalized) {
    normalizedBarcode.value = normalized;
  } else if (shouldShowInputError.value) {
    error.value = SCAN_TOKEN_ERROR_MESSAGE;
  }
}

/**
 * Handle scan submission (Enter key or programmatic call)
 */
function handleScan() {
  if (!barcodeValue.value.trim()) {
    error.value = 'Please enter a barcode';
    return;
  }

  const normalized = normalizeBarcode(barcodeValue.value);
  if (!normalized) {
    error.value = SCAN_TOKEN_ERROR_MESSAGE;
    return;
  }

  error.value = null;
  emit('scan', normalized);
}

/**
 * Reset scanner state
 */
function reset() {
  barcodeValue.value = '';
  normalizedBarcode.value = null;
  error.value = null;
  // Don't focus on reset to prevent keyboard popup
}

/**
 * Handle touch events - prevent keyboard popup on mobile/iPad
 */
function handleTouch(event: TouchEvent) {
  event.preventDefault();
  // Allow scanners to still input via keyboard events
}

/**
 * Focus the input field (for programmatic scanning)
 */
function focus() {
  if (barcodeInput.value) {
    // Focus is allowed for barcode scanners
    // inputmode="none" will prevent virtual keyboard
    barcodeInput.value.focus();
  }
}

/**
 * Set input value programmatically (for barcode scanners)
 */
function setValue(value: string) {
  barcodeValue.value = value;
  handleInput();
}

// Expose methods for parent component
defineExpose({
  reset,
  focus,
  setValue,
});

// Watch for external value changes (if needed)
watch(barcodeValue, handleInput);
</script>

<style scoped>
.barcode-scanner {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  width: 100%;
  max-width: 100%;
}

.barcode-label {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-weight: 600;
  font-size: 1.0625rem;
  color: #374151;
  margin-bottom: 0.75rem;
}

.barcode-label i {
  color: #667eea;
  font-size: 1.375rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.barcode-input {
  width: 100%;
  padding: 1.25rem 1.5rem;
  padding-right: 3.5rem;
  font-size: 1.75rem;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell',
    'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  font-weight: 600;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.25rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.875rem;
  background: #f9fafb;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  cursor: default;
}

.barcode-input:focus {
  outline: none;
  border-color: #667eea;
  background: white;
  box-shadow:
    0 0 0 4px rgba(102, 126, 234, 0.1),
    0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.barcode-input::placeholder {
  letter-spacing: 0.125rem;
  color: #9ca3af;
  font-weight: 400;
  text-transform: none;
  font-size: 1.5rem;
}

.barcode-input:not(:placeholder-shown) {
  background: white;
  border-color: #d1d5db;
}

.input-indicator {
  position: absolute;
  right: 1.25rem;
  top: 50%;
  transform: translateY(-50%);
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 1rem;
  pointer-events: none;
  z-index: 1;
}

.input-indicator.valid {
  background-color: #d1fae5;
  color: #059669;
}

.input-indicator.error {
  background-color: #fee2e2;
  color: #dc2626;
}

.error-container {
  min-height: 3.5rem;
  display: flex;
  align-items: flex-start;
  margin-top: 0.5rem;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  color: #dc2626;
  font-size: 1rem;
  padding: 0.75rem 1rem;
  background-color: #fef2f2;
  border-radius: 0.625rem;
  border: 1px solid #fecaca;
  width: 100%;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.error-message i {
  font-size: 1.25rem;
}

.button-group {
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  width: 100%;
}

.btn-clear,
.btn-confirm {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 1.5rem;
  font-size: 1.375rem;
  font-weight: 600;
  border-radius: 0.75rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.btn-clear {
  background-color: #f3f4f6;
  color: #374151;
}

.btn-clear:hover:not(:disabled) {
  background-color: #e5e7eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn-clear:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-confirm {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-confirm:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #d1d5db;
}

.btn-clear i,
.btn-confirm i {
  font-size: 1.125rem;
}

/* Tablet/iPad specific styles */
@media (min-width: 768px) and (max-width: 1024px) {
  .barcode-scanner {
    gap: 0.5rem;
  }

  .barcode-label {
    font-size: 0.875rem;
    margin-bottom: 0.375rem;
  }

  .barcode-label i {
    font-size: 1.125rem;
  }

  .barcode-input {
    padding: 0.75rem 1rem;
    padding-right: 2.5rem;
    font-size: 1.125rem;
    letter-spacing: 0.1rem;
  }

  .barcode-input::placeholder {
    font-size: 1rem;
    letter-spacing: 0.08rem;
  }

  .input-indicator {
    width: 1.5rem;
    height: 1.5rem;
    font-size: 0.875rem;
    right: 0.875rem;
  }

  .error-container {
    min-height: 0;
    margin-top: 0.25rem;
  }

  .error-message {
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
  }

  .error-message i {
    font-size: 1rem;
  }

  .button-group {
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  .btn-clear,
  .btn-confirm {
    padding: 0.75rem 1rem;
    font-size: 0.9375rem;
  }

  .btn-clear i,
  .btn-confirm i {
    font-size: 0.9375rem;
  }
}
</style>
